/**
 * Enviador de Mensagens
 * 
 * Responsável por:
 * - Gerar IDs únicos para requisições
 * - Montar payload final de mensagens
 * - Enviar mensagens via WebView
 * - Validar tamanho antes de enviar
 * 
 * @module MessageSender
 */

import { WebViewMessage, WebViewResponse } from '../../types/messages';
import { MAX_PAYLOAD_BYTES } from '../../constants';
import { sendToTerminal } from '../../../terminalLogger';
import { postJsonToWebView } from '../../transport';
import { LogCategory, getLogLevel, shouldLog } from '@/config/logging';

/**
 * Gerenciador de envio de mensagens
 */
export class MessageSender {
  private requestCounter = 0; // Garante IDs únicos mesmo no mesmo milissegundo

  /**
   * Gera um ID único para a requisição.
   * Usa timestamp + contador monotônico + sufixo aleatório para evitar colisão.
   */
  public generateRequestId(): string {
    this.requestCounter = (this.requestCounter + 1) % 1_000_000;
    const ts = Date.now();
    const rand = Math.random().toString(36).slice(2, 8);
    return `req-${ts}-${this.requestCounter}-${rand}`;
  }

  /**
   * Envia uma mensagem para o WebView
   * 
   * @param message Mensagem completa a ser enviada
   * @returns ID da requisição se enviada com sucesso, null caso contrário
   */
  public send(message: WebViewMessage, callback?: (response: WebViewResponse) => void): string | null {
    try {
      const jsonPayload = JSON.stringify(message);

      // Validar tamanho
      if (jsonPayload.length > MAX_PAYLOAD_BYTES) {
        const oversizedBy = jsonPayload.length - MAX_PAYLOAD_BYTES;
        const suggestedAction = message.action?.includes('nesting') 
          ? 'Sugestão: Use formato binário (PP01) ou reduza o número de figuras'
          : 'Sugestão: Quebre a operação em lotes menores';
        
        sendToTerminal('❌ [MESSAGE_SENDER] Payload muito grande', {
          action: message.action,
          requestId: message.requestId,
          payloadSize: jsonPayload.length,
          maxSize: MAX_PAYLOAD_BYTES,
          oversizedBy,
          oversizedPercent: ((jsonPayload.length / MAX_PAYLOAD_BYTES) * 100).toFixed(1) + '%',
          suggestion: suggestedAction
        }, LogCategory.ERROR);

        // Notificar callback de erro com mensagem detalhada
        if (callback) {
          try {
            callback({
              requestId: message.requestId,
              status: 'erro',
              message: `Payload muito grande (${(jsonPayload.length / 1024 / 1024).toFixed(2)}MB > ${(MAX_PAYLOAD_BYTES / 1024 / 1024).toFixed(0)}MB). ${suggestedAction}`
            } as unknown as WebViewResponse);
          } catch (error) {
            sendToTerminal('❌ [MESSAGE_SENDER] Erro no callback', {
              requestId: message.requestId,
              error: error instanceof Error ? error.message : String(error)
            }, LogCategory.ERROR);
          }
        }

        return null;
      }

      // Enviar
      const transportSent = postJsonToWebView(jsonPayload);
      if (!transportSent) {
        sendToTerminal('❌ [MESSAGE_SENDER] Transporte recusou mensagem', {
          action: message.action,
          requestId: message.requestId,
          payloadSize: jsonPayload.length
        }, LogCategory.ERROR);

        if (callback) {
          try {
            callback({
              requestId: message.requestId,
              status: 'erro',
              message: 'Falha no transporte WebView2',
              code: 'WEBVIEW_TRANSPORT_FAILED'
            } as unknown as WebViewResponse);
          } catch (error) {
            sendToTerminal('❌ [MESSAGE_SENDER] Erro no callback', {
              requestId: message.requestId,
              error: error instanceof Error ? error.message : String(error)
            }, LogCategory.ERROR);
          }
        }

        return null;
      }

      // Não logar health checks (muito frequentes - 4x por minuto)
      if (message.action !== 'health:pong') {
        const level = getLogLevel();
        // Verificar se deve logar categoria PAYLOAD no nível atual
        if (shouldLog(LogCategory.PAYLOAD, level)) {
          sendToTerminal('📤 [MESSAGE_SENDER] Mensagem enviada', {
            action: message.action,
            requestId: message.requestId,
            payloadSize: jsonPayload.length
          }, LogCategory.PAYLOAD);
        }
      }

      return message.requestId;
    } catch (error) {
      sendToTerminal('❌ [MESSAGE_SENDER] Erro ao enviar mensagem', {
        action: message.action,
        requestId: message.requestId,
        error: error instanceof Error ? error.message : String(error)
      }, LogCategory.ERROR);

      // Notificar callback de erro
      if (callback) {
        try {
          callback({
            requestId: message.requestId,
            status: 'erro',
            message: 'Erro ao enviar mensagem'
          } as unknown as WebViewResponse);
        } catch (callbackError) {
          sendToTerminal('❌ [MESSAGE_SENDER] Erro no callback', {
            requestId: message.requestId,
            error: callbackError instanceof Error ? callbackError.message : String(callbackError)
          }, LogCategory.ERROR);
        }
      }

      return null;
    }
  }

  /**
   * Envia uma mensagem com chave de idempotência
   * 
   * @param message Mensagem completa
   * @param idempotencyKey Chave de idempotência
   * @param callback Callback opcional
   * @returns ID da requisição se enviada com sucesso, null caso contrário
   */
  public sendWithIdempotency(
    message: WebViewMessage,
    idempotencyKey: string,
    callback?: (response: WebViewResponse) => void
  ): string | null {
    try {
      // Adicionar idempotencyKey ao envelope
      const envelope = {
        ...message,
        idempotencyKey
      };

      const jsonPayload = JSON.stringify(envelope);

      // Validar tamanho
      if (jsonPayload.length > MAX_PAYLOAD_BYTES) {
        sendToTerminal('❌ [MESSAGE_SENDER] Payload muito grande (com idempotência)', {
          action: message.action,
          requestId: message.requestId,
          payloadSize: jsonPayload.length,
          maxSize: MAX_PAYLOAD_BYTES,
          hasIdempotency: true
        }, LogCategory.ERROR);

        // Notificar callback de erro
        if (callback) {
          try {
            callback({
              requestId: message.requestId,
              status: 'erro',
              message: 'Payload muito grande'
            } as unknown as WebViewResponse);
          } catch (error) {
            sendToTerminal('❌ [MESSAGE_SENDER] Erro no callback', {
              requestId: message.requestId,
              error: error instanceof Error ? error.message : String(error)
            }, LogCategory.ERROR);
          }
        }

        return null;
      }

      // Enviar
      const transportSent = postJsonToWebView(jsonPayload);
      if (!transportSent) {
        sendToTerminal('❌ [MESSAGE_SENDER] Transporte recusou mensagem (com idempotência)', {
          action: message.action,
          requestId: message.requestId,
          idempotencyKey,
          payloadSize: jsonPayload.length
        }, LogCategory.ERROR);

        if (callback) {
          try {
            callback({
              requestId: message.requestId,
              status: 'erro',
              message: 'Falha no transporte WebView2',
              code: 'WEBVIEW_TRANSPORT_FAILED'
            } as unknown as WebViewResponse);
          } catch (error) {
            sendToTerminal('❌ [MESSAGE_SENDER] Erro no callback', {
              requestId: message.requestId,
              error: error instanceof Error ? error.message : String(error)
            }, LogCategory.ERROR);
          }
        }

        return null;
      }

      // Não logar health checks (muito frequentes - 4x por minuto)
      if (message.action !== 'health:pong') {
        const level = getLogLevel();
        // Verificar se deve logar categoria PAYLOAD no nível atual
        if (shouldLog(LogCategory.PAYLOAD, level)) {
          sendToTerminal('📤 [MESSAGE_SENDER] Mensagem enviada (com idempotência)', {
            action: message.action,
            requestId: message.requestId,
            idempotencyKey,
            payloadSize: jsonPayload.length
          }, LogCategory.PAYLOAD);
        }
      }

      return message.requestId;
    } catch (error) {
      sendToTerminal('❌ [MESSAGE_SENDER] Erro ao enviar mensagem (com idempotência)', {
        action: message.action,
        requestId: message.requestId,
        error: error instanceof Error ? error.message : String(error)
      }, LogCategory.ERROR);

      // Notificar callback de erro
      if (callback) {
        try {
          callback({
            requestId: message.requestId,
            status: 'erro',
            message: 'Erro ao enviar mensagem'
          } as unknown as WebViewResponse);
        } catch (callbackError) {
          sendToTerminal('❌ [MESSAGE_SENDER] Erro no callback', {
            requestId: message.requestId,
            error: callbackError instanceof Error ? callbackError.message : String(callbackError)
          }, LogCategory.ERROR);
        }
      }

      return null;
    }
  }
}

